import { useState } from "react";
import { Search, MapPin, CalendarDays, Users } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { properties, destinations } from "@/data/site";
import { validateSearch, todayISO } from "@/lib/booking";

export default function QuickSearch() {
  const { t, scrollToId, setBookingPrefill } = useApp();
  const [form, setForm] = useState({ destination: "", checkin: "", checkout: "", guests: 2 });
  const [errors, setErrors] = useState({});

  const submit = (e) => {
    e.preventDefault();
    const errs = validateSearch({ checkin: form.checkin, checkout: form.checkout, adults: Number(form.guests) });
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error(Object.values(errs)[0]); return; }
    setBookingPrefill({ destination: form.destination, checkin: form.checkin, checkout: form.checkout, adults: Number(form.guests) });
    toast.success("Dati trasferiti al motore di prenotazione.");
    scrollToId("booking");
  };

  const field = "w-full bg-transparent outline-none text-sm text-brand-black placeholder:text-brand-brown/60 py-1";
  return (
    <section id="quick-search" className="relative z-30 px-6">
      <form
        data-testid="quick-search-form"
        onSubmit={submit}
        className="max-w-5xl mx-auto -mt-20 md:-mt-16 bg-white rounded-2xl shadow-xl shadow-black/10 border border-brand-ivory p-4 md:p-3 grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr_0.8fr_auto] gap-3 md:gap-2 md:items-center"
      >
        <label className="flex items-center gap-3 px-3 py-2 md:border-r border-brand-ivory">
          <MapPin className="w-4 h-4 text-brand-gold shrink-0" />
          <span className="sr-only">{t.search.destination}</span>
          <select data-testid="qs-destination" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className={field}>
            <option value="">{t.search.destination}</option>
            <optgroup label="Destinazioni">
              {destinations.map((d) => <option key={d.id} value={d.filterCity}>{d.name}</option>)}
            </optgroup>
            <optgroup label="Strutture">
              {properties.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
            </optgroup>
          </select>
        </label>

        <label className="flex items-center gap-3 px-3 py-2 md:border-r border-brand-ivory">
          <CalendarDays className="w-4 h-4 text-brand-gold shrink-0" />
          <span className="sr-only">{t.search.checkin}</span>
          <input data-testid="qs-checkin" type="date" min={todayISO()} value={form.checkin} onChange={(e) => setForm({ ...form, checkin: e.target.value })} className={field} aria-label={t.search.checkin} />
        </label>

        <label className="flex items-center gap-3 px-3 py-2 md:border-r border-brand-ivory">
          <CalendarDays className="w-4 h-4 text-brand-gold shrink-0" />
          <span className="sr-only">{t.search.checkout}</span>
          <input data-testid="qs-checkout" type="date" min={form.checkin || todayISO()} value={form.checkout} onChange={(e) => setForm({ ...form, checkout: e.target.value })} className={field} aria-label={t.search.checkout} />
        </label>

        <label className="flex items-center gap-3 px-3 py-2 md:border-r border-brand-ivory">
          <Users className="w-4 h-4 text-brand-gold shrink-0" />
          <span className="sr-only">{t.search.guests}</span>
          <select data-testid="qs-guests" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className={field} aria-label={t.search.guests}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n} {n === 1 ? "ospite" : "ospiti"}</option>)}
          </select>
        </label>

        <button data-testid="qs-submit" type="submit" className="inline-flex items-center justify-center gap-2 bg-brand-olive text-white hover:bg-brand-black transition-colors rounded-xl px-6 py-3 font-medium">
          <Search className="w-4 h-4" /> {t.search.submit}
        </button>
      </form>
    </section>
  );
}
